import React, { useContext, useState } from "react";
import NumberFormat from "react-number-format";

import { ErrorMessage, Field, FormikContext } from "formik";

import {
  Select,
  MultiSelect,
  DateInput,
  PhoneInput,
  CountryInput,
  NationalityInput,
  InputWithSuffix,
  MoneyInput,
  MaskInput,
  AvatarInput,
  GalleryUpload,
  ShowPasswordButton,
  Ticks,
  SwitchInput,
  MoneyDropdownInput,
  StoreSelect,
  UserSearchSelect,
} from "./index.jsx";
import { UserContext } from '../contexts/index.jsx';
import { noAutofillProps } from '../core/noAutofill';

const FieldGroup = (props) => {
  const { user } = useContext(UserContext);
  const noFormik = props.noFormik || false;
  // Read unconditionally, and via the context rather than useFormikContext():
  // the hook throws when there is no Formik form above it, and this component
  // renders both inside and outside one (noFormik). Undefined here simply means
  // "no form", which is what the numberFormat branch below already handles.
  const formik = useContext(FormikContext);

  const [fieldType, setFieldType] = useState(props.type || "text");
  const fieldProps = props.field || {};

  switch (props.type) {
    case "avatar":
      fieldProps.component = AvatarInput;
      break;
    case "date":
      fieldProps.component = DateInput;
      fieldProps.onChange = props.onChange;
      fieldProps.value = props.value;
      fieldProps.dateFormat = props.dateFormat;
      break;
    case "country":
      fieldProps.component = CountryInput;
      fieldProps.onSelectionChange = props.onChange;
      break;
    case "galleryUpload":
      fieldProps.component = GalleryUpload;
      fieldProps.rules = props.rules;
      fieldProps.value = props.value;
      fieldProps.onDeleted = props.onDeleted;
      fieldProps.onChange = props.onChange;
      fieldProps.reflectOrder = props.reflectOrder;
      fieldProps.slotLabels = props.slotLabels;
      break;
    case "nationality":
      fieldProps.component = NationalityInput;
      fieldProps.onSelectionChange = props.onChange;
      break;
    case "money":
      fieldProps.component = MoneyInput;
      fieldProps.currency = props.currency;
      break;
    case "moneyDropdown":
      fieldProps.component = MoneyDropdownInput;
      fieldProps.currency = props.currency;
      fieldProps.defaultOptions = props.defaultOptions;
      break;
    case "masked":
      fieldProps.component = MaskInput;
      fieldProps.mask = props.mask;
      // Was falling through into `number`, handing every masked field the
      // number input's step/min. Nothing in the switch cascades on purpose.
      break;
    case "number":
      fieldProps.step = props.step;
      fieldProps.min = props.min;
      break;
    case "numberFormat":
      fieldProps.displayType = "input";
      fieldProps.thousandSeparator = ",";
      fieldProps.value = props.value;
      break;
    case "phone":
      fieldProps.component = PhoneInput;
      fieldProps.defaultCountry = "GB";
      fieldProps.showVerification = props.showVerification;
      fieldProps.onVerificationCancelled = props.onVerificationCancelled;
      fieldProps.rcCountrySelect = props.rcCountrySelect;
      break;
    case "storeSelect":
      fieldProps.component = StoreSelect;
      fieldProps.value = props.value;
      fieldProps.onSelectionChange = props.onChange;
      fieldProps.disabled = props.disabled;
      break;
    case "userSearchSelect":
      fieldProps.component = UserSearchSelect;
      fieldProps.value = props.value;
      fieldProps.defaultOptions = props.defaultOptions;
      fieldProps.onChange = props.onChange;
      break;
    case "select":
      fieldProps.component = Select;
      // Renders components/ui/Select. `legacySelect` keeps react-select for the
      // few call sites that need its `components` / `initiallyOpen` hooks.
      fieldProps.legacy = props.legacySelect;
      fieldProps.onSelectionChange = props.onChange;
      fieldProps.readOnly = props.readOnly;
      fieldProps.disabled = props.disabled;
      fieldProps.options = props.options;
      fieldProps.optionsUrl = props.optionsUrl;
      fieldProps.optionsMethod = props.optionsMethod;
      fieldProps.prependStaticOptions = props.prependStaticOptions;
      fieldProps.creatable = props.creatable;
      fieldProps.isMulti = props.isMulti;
      fieldProps.components = props.components;
      fieldProps.filterOption = props.filterOption;
      fieldProps.hideSelectedOptions = props.hideSelectedOptions;
      fieldProps.maxMenuHeight = props.maxMenuHeight;
      fieldProps.defaultValue = props.defaultValue;
      fieldProps.isSearchable = props.isSearchable;
      fieldProps.menuSearch = props.menuSearch;
      fieldProps.menuSearchPlaceholder = props.menuSearchPlaceholder;
      fieldProps.initiallyOpen = props.initiallyOpen;
      fieldProps.formatOptionLabel = props.formatOptionLabel;
      fieldProps.styles = props.inputStyle
        ? {
            singleValue: (base) => ({ ...base, ...props.inputStyle }),
          }
        : undefined;
      if (noFormik) {
        fieldProps.field = {
          name: props.name,
          value: props.value,
        };
      }
      break;
    case "multiselect":
      fieldProps.component = MultiSelect;
      fieldProps.mode = props.mode;
      fieldProps.maxSelections = props.maxSelections;
      fieldProps.allowAddition = props.allowAddition;
      fieldProps.onSelectionChange = props.onChange;
      fieldProps.defaultOptions = props.options;
      break;
    case "withSuffix":
      fieldProps.component = InputWithSuffix;
      fieldProps.suffix = props.suffix;
      break;
    case "ticks":
      fieldProps.options = props.options;
      fieldProps.component = Ticks;
      break;
    case "switch":
      fieldProps.component = SwitchInput;
      fieldProps.label = props.label;
      fieldProps.switchProps = {
        checkedIcon: false,
        uncheckedIcon: false,
        offHandleColor: "#000",
        offColor: "#FFF",
        onColor: "#0F66E7",
        height: 21,
        width: 41,
        handleDiameter: 16,
      };
      break;
  }

  if (fieldType === "text" || fieldType === "password") {
    fieldProps.maxLength = props.maxLength || 255;
  }

  const switchType = () => {
    setFieldType(fieldType === "text" ? "password" : "text");
  };

  const name = props.nameObject
    ? `${props.nameObject}.${props.name}`
    : props.name;

  let content;

  if (fieldType === "switch") {
    return (
      <Field
        id={name}
        type={fieldType}
        name={name}
        placeholder={props.placeholder}
        readOnly={props.readOnly}
        disabled={props.disabled}
        {...fieldProps}
      />
    );
  }

  if (fieldType === "radio") {
    const radioInputs = props.options.map((o) => {
      return (
        <label key={o.id}>
          <Field
            type="radio"
            name={name}
            value={o.id}
            readOnly={props.readOnly}
            disabled={props.disabled}
          />
          {o.name}
        </label>
      );
    });

    content = <div role="group">{radioInputs}</div>;
  } else {
    if (props.type === "numberFormat") {
      const { setFieldValue, values: formikValues } = (noFormik ? null : formik) ?? {};

      if (!fieldProps.value) {
        delete fieldProps.value;
      }

      content = (
        <>
          <NumberFormat
            {...{
              id: name,
              onValueChange: ({ value }) => {
                setFieldValue?.(name, value);
                props.onChange && props.onChange(value);
              },
              style: props.inputStyle,
              placeholder: !props.persistLabel ? props.placeholder : "",
              readOnly: props.readOnly,
              disabled: props.disabled,
              value: formikValues?.[name],
              ...fieldProps,
            }}
          />
          {props.persistLabel && (
            <label className="persistent-label" htmlFor={name}>
              {props.placeholder}
            </label>
          )}
        </>
      );
    } else {
      const FieldComponent = !noFormik
        ? Field
        : fieldType === "textarea"
        ? "textarea"
        : fieldProps.component
        ? fieldProps.component
        : "input";

      if (noFormik) {
        delete fieldProps.component;
        fieldProps.onChange = props.onChange;
        fieldProps.value = props.value;
      }

      content = (
        <>
          <FieldComponent
            id={name}
            as={!noFormik && fieldType === "textarea" ? "textarea" : undefined}
            type={fieldType}
            name={name}
            placeholder={!props.persistLabel ? props.placeholder : ""}
            readOnly={props.readOnly}
            disabled={props.disabled}
            style={props.inputStyle}
            // Opt-out of the browser's own autofill. Staff enter many DIFFERENT
            // people's details into the same form on the same origin, so the
            // browser offers one client's address or phone number while another
            // client's record is open — see noAutofillProps().
            {...(props.noAutofill ? noAutofillProps(name) : {})}
            {...fieldProps}
          />
          {props.persistLabel && (
            <label className="persistent-label" htmlFor={name}>
              {props.placeholder}
            </label>
          )}
        </>
      );
    }
  }

  const containerProps = {};

  if (props.tooltip) {
    containerProps["data-tip"] = props.tooltip;
  }

  if (props.bare) {
    const bareInner = (
      <>
        {props.label && (
          <label htmlFor={name}>
            {props.label} {props.required && "*"}
            {props.maxSelections && (
              <span>({props.maxSelections} options max)</span>
            )}
          </label>
        )}

        {content}

        {props.type === "password" && (
          <ShowPasswordButton className={fieldType} onClick={switchType} />
        )}

        {!noFormik && (
          <ErrorMessage name={name} component="div" className="error" />
        )}
      </>
    );

    const bareClassName = [
      props.className,
      props.type === "multiselect" ? "field-group-multiselect" : "",
      props.type === "phone" ? "field-group-phone" : "",
      props.tooltip ? "w-100" : "",
    ]
      .filter(Boolean)
      .join(" ");

    if (bareClassName || props.tooltip) {
      return (
        <div className={bareClassName || undefined} {...containerProps}>
          {bareInner}
        </div>
      );
    }

    return bareInner;
  }

  return (
    <div
      className={`field-group field-group-${fieldType} ${props.className ||
        ""}`}
      {...containerProps}
    >
      {props.label && (
        <label htmlFor={name}>
          {props.label} {props.required && "*"}
          {props.maxSelections && (
            <span>({props.maxSelections} options max)</span>
          )}
        </label>
      )}

      <div className="input-ct">
        {content}

        {props.type === "password" && (
          <ShowPasswordButton className={fieldType} onClick={switchType} />
        )}
      </div>

      {!noFormik && (
        <ErrorMessage name={name} component="div" className="error" />
      )}
    </div>
  );
};

export default FieldGroup;
